import { Checkbox, Grid, GridColumn, GridRow, Input, Select, SelectOptionType } from '@bp/ui-components';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useCreateSelectOptions } from '../../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { LessonTableType } from '../../TimetableVersion/Table/TimetableVersionLessonsTable';
import { LessonFormType } from '../../types';

type LessonBasicsFormProps = {
  status: LessonTableType['editStatus'];
  timetableDisabled?: boolean | null;
};

export const LessonBasicsForm = ({ status, timetableDisabled }: LessonBasicsFormProps) => {
  const { t } = useTranslation();

  const { setFieldTouched, setFieldValue, values, handleChange } = useFormikContext<LessonFormType>();

  const { subjectData } = useLoadBasicData({ pause: false });

  const subjectsOptions = useCreateSelectOptions(
    subjectData?.subjects.filter((s) => {
      return s.epochEnabled || s.subjectHourEnabled;
    }),
    'uuid',
    'name',
  );

  const subjectValue = subjectsOptions.find((option) => {
    return option.value === values.subject?.uuid;
  });

  return (
    <Grid useFormGap>
      <GridRow>
        <GridColumn width={10}>
          <GridRow spacingBottom='s'>
            <Select
              options={subjectsOptions}
              readonly={status === 'blocked'}
              name='subject'
              isSearchable
              onChange={async (option) => {
                const opt = option as SingleValue<SelectOptionType>;
                await setFieldTouched(`subject`, true);
                if (opt) {
                  const subject = subjectData?.subjects.find((s) => s.uuid === opt.value);
                  await setFieldValue(`subject`, { uuid: subject?.uuid, name: subject?.name }, true);
                } else {
                  await setFieldValue(`subject`, null, true);
                }
              }}
              value={subjectValue}
              label={t('lesson.basics.name')}
            />
          </GridRow>
          <GridRow spacingTop='s'>
            <GridColumn width={6}>
              <Input
                value={values.subTitle}
                name='subTitle'
                onChange={handleChange}
                label={t('lesson.basics.subTitle')}
                readonly={status === 'blocked'}
              />
            </GridColumn>
            <GridColumn width={6}>
              <Input
                value={values.comment}
                name='comment'
                onChange={handleChange}
                label={t('lesson.basics.comment')}
                readonly={status === 'blocked'}
              />
            </GridColumn>
          </GridRow>
        </GridColumn>

        <GridColumn width={2} className='mt-3'>
          <Checkbox
            name='teachingLoadEnabled'
            checked={values.teachingLoadEnabled ?? false}
            label={t('lesson.basics.isTeachingLoadEnabled.full')}
            onChange={async (event) => {
              await setFieldValue(`teachingLoadEnabled`, event.target.checked, true);
              await setFieldTouched(`teachingLoadEnabled`, true);
            }}
          />
          <Checkbox
            name='timetableEnabled'
            disabled={status === 'placedCards' || status === 'blocked' || status === 'inVersion' || !!timetableDisabled}
            checked={values.timetableEnabled ?? false}
            label={t('lesson.basics.isTimetableEnabled.full')}
            onChange={async (event) => {
              await setFieldValue(`timetableEnabled`, event.target.checked, true);
              await setFieldTouched(`timetableEnabled`, true);
            }}
          />
          <Checkbox
            name='elective'
            disabled={status === 'blocked'}
            checked={values.elective ?? false}
            label={t('lesson.basics.isElective')}
            onChange={async (event) => {
              await setFieldValue(`elective`, event.target.checked, true);
              await setFieldTouched(`elective`, true);
            }}
          />
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
